// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
// --- Logic --------------------------------------------------------------------------------------
type TySpeechResult = { isFinal: boolean; 0: { transcript: string } };
type TySpeechEvent = { resultIndex: number; results: { length: number } & Record<number, TySpeechResult> };
type TyRecognition = {
    lang: string;
    continuous: boolean;
    interimResults: boolean;
    start: () => void;
    stop: () => void;
    onresult: ((event: TySpeechEvent) => void) | null;
    onerror: (() => void) | null;
    onend: (() => void) | null;
};

/** Chromium and Safari ship it under two names, and Firefox ships neither. */
const getEngine = (): (new () => TyRecognition) | null => {
    const win = window as unknown as { SpeechRecognition?: new () => TyRecognition; webkitSpeechRecognition?: new () => TyRecognition };
    return win.SpeechRecognition ?? win.webkitSpeechRecognition ?? null;
};

/**
 * Speech to text, in the browser, for the one field where typing is the thing that stops people
 * using a CRM: the summary of a call they have just put the phone down on.
 *
 * It is the **browser's own** recognizer — no key, no upload, no provider — which is why it is worth
 * having at all. The price is that Firefox has none, so the button that uses this hides itself
 * rather than failing when pressed, and that the engine stops on its own after a pause, which is why
 * `isListening` is driven by `onend` rather than by the press that started it.
 *
 * Results arrive as a growing list where only the `isFinal` ones are settled, so the hook hands back
 * each finished phrase once and leaves the field to append it. Reading the whole list every event
 * would re-append everything already typed.
 */
export default function useDictation(onPhrase: (text: string) => void) {
    const lang = useLang((state) => state.lang) ?? "ar";
    const [isListening, setIsListening] = useState(false);
    const engineRef = useRef<TyRecognition | null>(null);
    /** Read inside the engine's callbacks, which are bound once and would otherwise keep the first one. */
    const phraseRef = useRef(onPhrase);

    const isSupported = !!getEngine();

    // Written in an effect rather than in the body: a ref touched during render is a value React is
    // allowed to throw away, and the rule that forbids it is right even where this one is harmless.
    useEffect(() => {
        phraseRef.current = onPhrase;
    });

    useEffect(() => {
        return () => engineRef.current?.stop();
    }, []);

    const stop = () => {
        engineRef.current?.stop();
        engineRef.current = null;
        setIsListening(false);
    };

    const start = () => {
        const Engine = getEngine();
        if (!Engine) return;

        const engine = new Engine();
        engine.lang = lang === "en" ? "en-US" : "ar-SA";
        engine.continuous = true;
        engine.interimResults = false;

        engine.onresult = (event) => {
            for (let index = event.resultIndex; index < event.results.length; index += 1) {
                const result = event.results[index];
                if (result?.isFinal) phraseRef.current(result[0].transcript.trim());
            }
        };
        engine.onerror = () => setIsListening(false);
        engine.onend = () => setIsListening(false);

        engineRef.current = engine;
        engine.start();
        setIsListening(true);
    };

    return { isSupported, isListening, start, stop, toggle: () => (isListening ? stop() : start()) };
}
